import { useState } from 'react';
import { Play, RefreshCw, ChevronDown, ChevronRight } from 'lucide-react';
import { Button, Card } from '@/components/ui';
import { parseLocalDateTime } from '@/lib/date';
import {
  useAdminLlmRuns,
  type LlmRun,
  type LlmResult,
  type RunDetail,
} from './hooks/useAdminLlmRuns';
import { RunStressTestModal } from './components/RunStressTestModal';

function formatRunDate(iso: string): string {
  return parseLocalDateTime(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatPassRate(rate: number | null): string {
  if (rate === null) return '—';
  return `${(rate * 100).toFixed(1)}%`;
}

function formatCost(cost: number | null): string {
  if (cost === null) return '—';
  return `$${cost.toFixed(4)}`;
}

function formatMs(ms: number | null): string {
  if (ms === null) return '—';
  return `${ms.toFixed(0)} ms`;
}

interface RunRowProps {
  run: LlmRun;
  password: string;
  fetchRunDetail: (runId: string) => Promise<RunDetail | null>;
}

function RunRow({ run, password: _password, fetchRunDetail }: RunRowProps) {
  const [expanded, setExpanded] = useState(false);
  const [detail, setDetail] = useState<RunDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const handleToggle = async () => {
    if (expanded) {
      setExpanded(false);
      return;
    }
    setExpanded(true);
    if (!detail) {
      setLoadingDetail(true);
      const d = await fetchRunDetail(run.id);
      setDetail(d);
      setLoadingDetail(false);
    }
  };

  return (
    <>
      <tr
        className="border-b border-border last:border-b-0 hover:bg-muted/50 cursor-pointer transition-colors"
        onClick={handleToggle}
      >
        <td className="p-4 text-muted-foreground">
          {expanded ? (
            <ChevronDown
              size={14}
              className="inline"
            />
          ) : (
            <ChevronRight
              size={14}
              className="inline"
            />
          )}
        </td>
        <td className="p-4 font-medium text-foreground">{run.name}</td>
        <td className="p-4 text-muted-foreground">{run.provider}</td>
        <td className="p-4 text-muted-foreground whitespace-nowrap">
          {formatRunDate(run.started_at)}
        </td>
        <td className="p-4 text-muted-foreground text-right">
          {run.fixture_count}
        </td>
        <td className="p-4 text-right font-medium">
          {formatPassRate(run.pass_rate)}
        </td>
        <td className="p-4 text-muted-foreground text-right">
          {formatMs(run.p95_ms)}
        </td>
        <td className="p-4 text-muted-foreground text-right">
          {formatCost(run.estimated_cost_usd)}
        </td>
      </tr>
      {expanded ? (
        <tr className="border-b border-border bg-muted/30">
          <td
            colSpan={8}
            className="px-4 pb-4"
          >
            {loadingDetail ? (
              <div className="text-sm text-muted-foreground py-2">
                Loading results...
              </div>
            ) : !detail ? (
              <div className="text-sm text-danger py-2">
                Failed to load run detail.
              </div>
            ) : detail.results.length === 0 ? (
              <div className="text-sm text-muted-foreground py-2">
                No results recorded for this run.
              </div>
            ) : (
              <ResultsTable results={detail.results} />
            )}
          </td>
        </tr>
      ) : null}
    </>
  );
}

function ResultsTable({ results }: { results: LlmResult[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs mt-2">
        <thead>
          <tr className="text-left text-muted-foreground border-b border-border">
            <th className="py-2 pr-4 font-medium">Fixture</th>
            <th className="py-2 pr-4 font-medium">Pass</th>
            <th className="py-2 pr-4 font-medium">Latency</th>
            <th className="py-2 font-medium">Assertion errors</th>
          </tr>
        </thead>
        <tbody>
          {results.map((r) => (
            <tr
              key={r.id}
              className="border-b border-border/50 last:border-b-0"
            >
              <td className="py-2 pr-4 font-mono text-foreground">
                {r.fixture_id}
              </td>
              <td className="py-2 pr-4">
                {r.assertions_pass === null ? (
                  <span className="text-muted-foreground">—</span>
                ) : r.assertions_pass ? (
                  <span className="text-success font-medium">pass</span>
                ) : (
                  <span className="text-danger font-medium">fail</span>
                )}
              </td>
              <td className="py-2 pr-4 text-muted-foreground">
                {r.latency_ms !== null ? `${r.latency_ms} ms` : '—'}
              </td>
              <td className="py-2 text-muted-foreground">
                {r.assertion_errors
                  ? JSON.stringify(r.assertion_errors).slice(0, 200)
                  : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export interface AdminLlmRunsProps {
  password: string;
}

export function AdminLlmRuns({ password }: AdminLlmRunsProps) {
  const { status, runs, error, lastFetchedAt, refetch, fetchRunDetail } =
    useAdminLlmRuns(password);
  const [runModalOpen, setRunModalOpen] = useState(false);

  if (status === 'loading' && runs.length === 0) {
    return (
      <div className="text-muted-foreground">
        Loading LLM stress-test runs...
      </div>
    );
  }

  if (status === 'error' && runs.length === 0) {
    return (
      <Card className="p-4 border-danger">
        <div className="text-sm text-danger">Failed to load runs: {error}</div>
        <Button
          variant="secondary"
          onClick={refetch}
          className="mt-3"
        >
          Retry
        </Button>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="text-xs text-muted-foreground">
          Last refreshed:{' '}
          {lastFetchedAt ? lastFetchedAt.toLocaleTimeString() : '—'}
        </div>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            onClick={refetch}
            disabled={status === 'loading'}
          >
            <RefreshCw
              className={`h-4 w-4 mr-2 ${status === 'loading' ? 'animate-spin' : ''}`}
            />
            Refresh
          </Button>
          <Button
            variant="primary"
            onClick={() => setRunModalOpen(true)}
            leftIcon={<Play size={14} />}
          >
            Run new test
          </Button>
        </div>
      </div>

      <RunStressTestModal
        open={runModalOpen}
        onClose={() => setRunModalOpen(false)}
        adminPassword={password}
        onSuccess={refetch}
      />

      {/* Empty state */}
      {runs.length === 0 ? (
        <Card className="p-8 text-center">
          <div className="text-muted-foreground text-sm">
            No runs yet. Run{' '}
            <code className="bg-muted px-1 py-0.5 rounded text-xs font-mono">
              deno task llm:run
            </code>{' '}
            from the repo root.
          </div>
        </Card>
      ) : (
        <Card
          variant="default"
          padding="none"
        >
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-muted-foreground">
                  <th className="p-4 w-8" />
                  <th className="p-4 font-medium">Name</th>
                  <th className="p-4 font-medium">Provider</th>
                  <th className="p-4 font-medium">Started</th>
                  <th className="p-4 font-medium text-right">Fixtures</th>
                  <th className="p-4 font-medium text-right">Pass rate</th>
                  <th className="p-4 font-medium text-right">p95</th>
                  <th className="p-4 font-medium text-right">Est. cost</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((run) => (
                  <RunRow
                    key={run.id}
                    run={run}
                    password={password}
                    fetchRunDetail={fetchRunDetail}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
